/**
 * Shared axios instance for all API modules. `withCredentials` lets the
 * browser send/receive the httpOnly refresh-token cookie set by the auth
 * endpoints, even if the frontend and API ever end up on different origins.
 */
import Axios from "axios";

const Client = Axios.create({
    withCredentials: true,
});

// client.js sits outside the React tree, so this is how a refreshed access
// token gets back into the store, and how the app finds out the session is
// really gone when the refresh token itself no longer works. The app root
// registers these once on mount (see AppRouter.js).
let authHandlers = {
    onTokenRefreshed: () => {},
    onRefreshFailed: () => {},
};

export const setAuthHandlers = (handlers) => {
    authHandlers = handlers;
};

// Concurrent 401s (several requests firing around the same time) share one
// refresh call instead of each kicking off its own.
let refreshPromise = null;

const refreshAccessToken = () => {
    if (!refreshPromise) {
        refreshPromise = Client.post('/api/auth/refresh')
            .then((res) => res.data)
            .finally(() => {
                refreshPromise = null;
            });
    }

    return refreshPromise;
};

// A request that 401s because its access token expired mid-session gets one
// automatic refresh-and-retry. A request with no Authorization header (or
// the refresh call itself, which never sends one) has no token to renew, so
// it's left alone - same for a request that's already been retried once.
Client.interceptors.response.use(
    (response) => response,
    (error) => {
        const originalRequest = error.config;
        const hadAuthHeader = !!originalRequest?.headers?.Authorization;

        if (error.response?.status !== 401 || !hadAuthHeader || originalRequest._retried) {
            return Promise.reject(error);
        }

        originalRequest._retried = true;

        return refreshAccessToken().then(({user, accessToken}) => {
            authHandlers.onTokenRefreshed(user, accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return Client(originalRequest);
        }).catch(() => {
            authHandlers.onRefreshFailed();
            return Promise.reject(error);
        });
    }
);

export default Client;
